import { useCallback, useEffect, useState } from "react";
import { ArrowRight, Link2, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { PageHeader } from "@/components/Layout";
import { AccountSelect } from "@/components/AccountSelect";
import { EmptyAccounts } from "@/components/EmptyAccounts";
import { useAccounts } from "@/hooks/useAccounts";
import { api, errMsg } from "@/lib/api";
import { ACCOUNT_TYPES, fmtPnl, pnlClass, fmtUtc } from "@/lib/trading";

const NewLink = ({ accounts, onCreated }) => {
  const [master, setMaster] = useState("");
  const [slave, setSlave] = useState("");
  const [mult, setMult] = useState("1");
  const create = async () => {
    if (!master || !slave) return toast.error("Pick a master and a slave account");
    try {
      await api.post("/copy-links", { master_account_id: master, slave_account_id: slave, multiplier: Number(mult), enabled: true });
      toast.success("Copy link created");
      setSlave(""); onCreated();
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="panel p-5 rise" data-testid="new-copy-link-form">
      <p className="text-xs font-mono uppercase tracking-wider text-slate-500 mb-4">New master → slave link</p>
      <div className="flex flex-col lg:flex-row lg:items-end gap-4">
        <div className="space-y-1.5 flex-1"><Label className="text-xs text-slate-400">Master (signal source)</Label><AccountSelect accounts={accounts} value={master} onChange={setMaster} testId="master-account-select" placeholder="Select master" /></div>
        <ArrowRight className="hidden lg:block h-5 w-5 mb-2.5 text-emerald-400" />
        <div className="space-y-1.5 flex-1"><Label className="text-xs text-slate-400">Slave (copier)</Label><AccountSelect accounts={accounts.filter((a) => a.id !== master)} value={slave} onChange={setSlave} testId="slave-account-select" placeholder="Select slave" /></div>
        <div className="space-y-1.5 w-full lg:w-32"><Label className="text-xs text-slate-400">Lot multiplier</Label><Input data-testid="multiplier-input" type="number" step="0.1" min="0.01" value={mult} onChange={(e) => setMult(e.target.value)} className="font-mono" /></div>
        <Button onClick={create} data-testid="create-copy-link-button" className="bg-emerald-600 hover:bg-emerald-500 text-white"><Link2 className="h-4 w-4 mr-1" />Link</Button>
      </div>
    </div>
  );
};

const LinkRow = ({ l, name, onChange }) => {
  const [mult, setMult] = useState(String(l.multiplier));
  const patch = async (body, msg) => {
    try { await api.patch(`/copy-links/${l.id}`, body); toast.success(msg); onChange(); } catch (e) { toast.error(errMsg(e)); }
  };
  const remove = async () => {
    if (!window.confirm("Remove this copy link? Already-copied trades stay in the journal.")) return;
    await api.delete(`/copy-links/${l.id}`); toast.success("Link removed"); onChange();
  };
  return (
    <div className="panel p-4 flex flex-col md:flex-row md:items-center gap-4" data-testid={`copy-link-${l.id}`}>
      <div className="flex items-center gap-3 flex-1 min-w-0">
        <div className="min-w-0"><p className="text-[10px] font-mono uppercase text-slate-500">Master</p><p className="font-medium truncate">{name(l.master_account_id)}</p></div>
        <ArrowRight className={`h-4 w-4 shrink-0 ${l.enabled ? "text-emerald-400 animate-pulse" : "text-slate-600"}`} />
        <div className="min-w-0"><p className="text-[10px] font-mono uppercase text-slate-500">Slave</p><p className="font-medium truncate">{name(l.slave_account_id)}</p></div>
      </div>
      <div className="flex items-center gap-2">
        <Input value={mult} onChange={(e) => setMult(e.target.value)} type="number" step="0.1" className="w-20 h-8 font-mono" data-testid={`link-multiplier-${l.id}`} />
        <span className="text-xs text-slate-500 font-mono">x</span>
        {Number(mult) !== l.multiplier && <Button size="sm" variant="outline" onClick={() => patch({ multiplier: Number(mult) }, "Multiplier updated")} data-testid={`save-multiplier-${l.id}`}>Save</Button>}
      </div>
      <div className="text-xs font-mono text-slate-400 md:w-36" data-testid={`link-stats-${l.id}`}>{l.copied_count} copied · <span className={pnlClass(l.copied_pnl)}>{fmtPnl(l.copied_pnl)}</span></div>
      <div className="flex items-center gap-3">
        <span className={`text-xs font-mono ${l.enabled ? "text-emerald-400" : "text-slate-500"}`}>{l.enabled ? "COPYING" : "PAUSED"}</span>
        <Switch checked={l.enabled} onCheckedChange={(v) => patch({ enabled: v }, v ? "Copy trading enabled" : "Copy trading paused")} data-testid={`copy-trading-toggle-${l.id}`} />
        <button onClick={remove} data-testid={`delete-link-${l.id}`} className="p-1 text-slate-500 hover:text-red-400"><Trash2 className="h-4 w-4" /></button>
      </div>
    </div>
  );
};

export default function CopyTrading() {
  const { accounts, loaded } = useAccounts();
  const [links, setLinks] = useState([]);
  const [log, setLog] = useState([]);
  const load = useCallback(() => {
    api.get("/copy-links").then((r) => setLinks(r.data));
    api.get("/copy-log").then((r) => setLog(r.data));
  }, []);
  useEffect(() => { load(); }, [load]);
  const name = (id) => { const a = accounts.find((x) => x.id === id); return a ? `${a.broker} #${a.account_number} (${ACCOUNT_TYPES[a.account_type].label})` : "—"; };

  if (loaded && accounts.length < 2) return (
    <><PageHeader title="Copy Trading" subtitle="You need at least two accounts to link a master and a slave." />{!accounts.length ? <EmptyAccounts /> : <p className="panel p-8 text-slate-400 text-sm" data-testid="need-two-accounts">Add one more account to start copying.</p>}</>
  );

  return (
    <>
      <PageHeader title="Copy Trading" subtitle="Simulated: every trade logged on a master is mirrored to its enabled slaves with the lot multiplier applied to lots and P&L." />
      <NewLink accounts={accounts} onCreated={load} />
      <div className="mt-6 space-y-3" data-testid="copy-links-list">
        <p className="text-xs font-mono uppercase tracking-wider text-slate-500">Active links ({links.filter((l) => l.enabled).length}/{links.length})</p>
        {!links.length && <p className="text-sm text-slate-500">No links yet.</p>}
        {links.map((l) => <LinkRow key={`${l.id}-${l.multiplier}`} l={l} name={name} onChange={load} />)}
      </div>
      <div className="panel mt-6 overflow-x-auto" data-testid="copy-log">
        <p className="text-xs font-mono uppercase tracking-wider text-slate-500 p-5 pb-3">Copy execution log</p>
        <table className="w-full text-sm min-w-[640px]">
          <thead><tr className="border-y border-slate-800 text-xs font-mono text-slate-500">{["Entry (UTC)", "Symbol", "Side", "Lots", "Slave account", "P&L"].map((h) => <th key={h} className="px-4 py-2 text-left font-medium">{h}</th>)}</tr></thead>
          <tbody>
            {log.map((t) => (
              <tr key={t.id} className="border-b border-slate-800/60 hover:bg-slate-800/30">
                <td className="px-4 py-2 font-mono text-xs text-slate-400">{fmtUtc(t.entry_time)}</td>
                <td className="px-4 py-2 font-mono font-semibold">{t.symbol}</td>
                <td className={`px-4 py-2 font-mono text-xs uppercase ${t.side === "buy" ? "text-emerald-400" : "text-red-400"}`}>{t.side}</td>
                <td className="px-4 py-2 font-mono text-xs">{t.lots}</td>
                <td className="px-4 py-2 text-xs text-slate-400">{name(t.account_id)}</td>
                <td className={`px-4 py-2 font-mono font-semibold ${pnlClass(t.pnl)}`}>{fmtPnl(t.pnl)}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {!log.length && <p className="p-6 text-sm text-slate-500">Nothing copied yet. Log a trade on a master account.</p>}
      </div>
    </>
  );
}
